import { IconButton } from '@components/core/icon-button.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { withFieldGroup } from '@components/ui/form/form';
import { cn } from '@lib/utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon, Trash2Icon } from 'lucide-react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import {
  ADVANCED_SCORING_TOOLTIPS,
  aggregationOptions,
  blockingYellowOptions,
  buildEmptyMoe,
  pendingMopOptions,
  yellowDispositionOptions,
} from './meia-measures.util';
import { type MeiaTemplateOption, MeiaTemplatePopover } from './meia-template-popover.component';

type MeiaMoeCardProps = {
  canEdit: boolean;
  hasError: boolean;
  moeOrdinal: number;
  moeTemplates: MeiaTemplateOption[];
  onRemove: () => void;
};

const defaultMoeCardProps: MeiaMoeCardProps = {
  canEdit: true,
  hasError: false,
  moeOrdinal: 1,
  moeTemplates: [],
  onRemove: () => undefined,
};

/**
 * Measure-of-Effectiveness card (accordion header, template picker, description, advanced
 * scoring controls) as a TanStack field group, so the single MoS modal and the inline
 * swimlane measures form mount the same card at their respective field paths.
 */
export const MeiaMoeCardGroup = withFieldGroup({
  defaultValues: buildEmptyMoe(),
  props: defaultMoeCardProps,
  render: function MeiaMoeCard({ canEdit, group, hasError, moeOrdinal, moeTemplates, onRemove }) {
    return (
      <div className={cn('rounded-md bg-gray-100 p-2', hasError && 'border border-red-500')}>
        <div className="rounded-md bg-white">
          <AccordionPrimitive.Root
            collapsible
            defaultValue="open"
            type="single"
          >
            <AccordionPrimitive.Item value="open">
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <AccordionPrimitive.Trigger className="group flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors hover:text-gray-700">
                  <ChevronDownIcon className="size-3 shrink-0 text-gray-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  MoE #{moeOrdinal}
                </AccordionPrimitive.Trigger>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    <MeiaTemplatePopover
                      onSelect={(template) => {
                        group.setFieldValue('description', template.description ?? template.name);
                        group.setFieldValue('moeTemplateCode', template.id);
                      }}
                      templates={moeTemplates}
                    />
                    <IconButton
                      ariaLabel="Remove MoE"
                      icon={<Trash2Icon />}
                      onClick={onRemove}
                      variant="destructive"
                    />
                  </div>
                )}
              </div>

              <AccordionPrimitive.Content className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="flex flex-col gap-3 p-3 pt-0">
                  <group.AppField name="description">
                    {(field) => (
                      <field.TextAreaField
                        disabled={!canEdit}
                        label="Description"
                        placeholder="Targets engaged inside contested airspace, sustained through electronic warfare."
                        required
                        rows={2}
                      />
                    )}
                  </group.AppField>

                  <Accordion
                    collapsible
                    type="single"
                  >
                    <AccordionItem
                      className="border-b-0"
                      value="advanced"
                    >
                      <AccordionTrigger className="ml-auto w-fit !flex-none gap-2 py-1 text-xs font-medium text-gray-600">
                        Advanced Scoring
                      </AccordionTrigger>
                      <AccordionContent className="pt-2">
                        <div className="flex flex-col gap-3">
                          <group.AppField name="weight">
                            {(field) => (
                              <field.TextField
                                {...MEIA_AUTOFILL_IGNORE_PROPS}
                                description="Leave empty to balance automatically across the other MoE."
                                disabled={!canEdit}
                                label="Weight (0–1)"
                                placeholder="Auto"
                                step="0.01"
                                tooltip={ADVANCED_SCORING_TOOLTIPS.weight}
                                type="number"
                              />
                            )}
                          </group.AppField>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <group.AppField name="aggregationMethod">
                              {(field) => (
                                <field.SelectField
                                  disabled={!canEdit}
                                  label="How MoPs combine"
                                  options={aggregationOptions}
                                  tooltip={ADVANCED_SCORING_TOOLTIPS.aggregationMethod}
                                />
                              )}
                            </group.AppField>
                            <group.AppField name="blockingYellowBehavior">
                              {(field) => (
                                <field.SelectField
                                  disabled={!canEdit}
                                  label="If a blocking Task is yellow"
                                  options={blockingYellowOptions}
                                  tooltip={ADVANCED_SCORING_TOOLTIPS.blockingYellowBehavior}
                                />
                              )}
                            </group.AppField>
                            <group.AppField name="pendingMopBehavior">
                              {(field) => (
                                <field.SelectField
                                  disabled={!canEdit}
                                  label="If a MoP is missing"
                                  options={pendingMopOptions}
                                  tooltip={ADVANCED_SCORING_TOOLTIPS.pendingMopBehavior}
                                />
                              )}
                            </group.AppField>
                            <group.AppField name="yellowDisposition">
                              {(field) => (
                                <field.SelectField
                                  disabled={!canEdit}
                                  label="When this MoE is yellow"
                                  options={yellowDispositionOptions}
                                  tooltip={ADVANCED_SCORING_TOOLTIPS.yellowDisposition}
                                />
                              )}
                            </group.AppField>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          </AccordionPrimitive.Root>
        </div>
      </div>
    );
  },
});
